import { useCallback, useEffect, useMemo, useTransition } from "react";

import { PlusIcon } from "lucide-react";
import { Link, useMatch, useNavigate, useSearchParams } from "react-router";

import { useFeatureAlert } from "@/components/helpers/custom-hooks/use-feature-alert";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Media } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { CertificationDrawer } from "@/components/pages_logged_in/certifications/CertificationDrawer";
import { CertificationsList } from "@/components/pages_logged_in/certifications/CertificationsList";
import { EmptyCertifications } from "@/components/pages_logged_in/certifications/EmptyCertifications";
import { useCreateCertification } from "@/components/pages_logged_in/certifications/useCreateCertification";
import { CertificationsSvg } from "@/components/pieces/icons/svg/certifications";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { CertificationsDocument, useCertificationsQuery } from "@/graphql";

import { NoSearchResults } from "./NoSearchResults";
import { Pagination } from "./Pagination";

function CertificationsHeader() {
  return (
    <div className="border-b justify-center bg-white py-4 flex md:py-8 shrink-0">
      <div className="max-w-content mx-4 w-full">
        <div className="flex items-center gap-4">
          <Media lessThan="sm">
            <CertificationsSvg size={24} />
          </Media>
          <Media greaterThanOrEqual="sm">
            <CertificationsSvg size={26} />
          </Media>
          <h1 className="m-0 font-serif text-xl sm:text-[28px]">
            Certifications
          </h1>
        </div>
        <div className="mt-2 text-gray-500">
          Manage and track certifications for your team
        </div>
      </div>
    </div>
  );
}

export function Certifications() {
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isWorkspaceAdmin();
  const isCreateOpen = useMatch("/certifications/create");

  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const name = searchParams.get("name");
  const size = 5;

  const [searchLoading, startSearchTransition] = useTransition();
  const [pageLoading, startPageTransition] = useTransition();

  const variables = useMemo(() => {
    return {
      page,
      size,
      name,
    };
  }, [page, size, name]);

  const { data, refetch, loading } = useCertificationsQuery({
    variables,
    skip: !currentWorkspace.certificationsEnabled || !isAdmin,
  });

  const { onOpenChange, ...createProps } = useCreateCertification({
    refetchQueries: [
      {
        query: CertificationsDocument,
        variables,
      },
    ],
    workspace: currentWorkspace,
  });

  const certifications = data?.currentWorkspace?.certifications.data || [];
  const total = data?.currentWorkspace?.certifications.total || 0;

  useEffect(() => {
    if (!currentWorkspace.certificationsEnabled || !isAdmin) {
      navigate("/dashboard");
    }
  }, [currentWorkspace.certificationsEnabled, navigate, isAdmin]);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (searchTerm) {
        newParams.set("name", searchTerm);
      } else {
        newParams.delete("name");
      }
      newParams.delete("page"); // Reset to page 1 (implied)
      startSearchTransition(() => {
        setSearchParams(newParams);
        refetch({
          page,
          size,
          name: searchTerm,
        });
      });
    },
    [searchParams, setSearchParams, startSearchTransition, refetch, page, size],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      if (newPage === 1) {
        newParams.delete("page"); // Page 1 is implied
      } else {
        newParams.set("page", newPage.toString());
      }
      startPageTransition(() => {
        setSearchParams(newParams);
        refetch({
          page: newPage,
          size,
          name,
        });
      });
    },
    [searchParams, setSearchParams, startPageTransition, refetch, size, name],
  );

  const handleClearSearch = useCallback(() => {
    handleSearch("");
  }, [handleSearch]);

  const { alertNode } = useFeatureAlert({
    featureType: "CERTIFICATIONS",
    title: "Recognize your team's growth",
    description: `Create meaningful recognition for your members as they develop competencies.
      Each certification you define provides formal validation of skills attained,
      increasing engagement and demonstrating the tangible value of your learning initiatives.`,
  });

  if (!currentWorkspace.certificationsEnabled || !isAdmin) {
    return null;
  }

  return (
    <div className="md:ml-sidebar flex flex-col h-full">
      <div className="flex flex-col sm:h-full sm:overflow-y-auto">
        <CertificationsHeader />
        <div className="mx-4 flex flex-col items-center gap-8 pt-8 sm:py-8 h-full min-h-0">
          {alertNode}
          {certifications.length === 0 &&
          !name &&
          !searchLoading &&
          !pageLoading &&
          !loading ? (
            <EmptyCertifications />
          ) : (
            <div className="flex flex-col gap-4 w-full max-w-content h-full">
              <div className="flex items-center justify-between gap-3 flex-shrink-0">
                <div className=" w-full sm:w-2xs xl:w-xl">
                  <SearchInput
                    value={name || ""}
                    onChange={handleSearch}
                    loading={searchLoading}
                    placeholder="Search by name"
                  />
                </div>
                <Media greaterThanOrEqual="sm">
                  <Button asChild>
                    <Link to="/certifications/create">
                      <PlusIcon className="h-4 w-4" />
                      Create Certification
                    </Link>
                  </Button>
                </Media>
                <Media lessThan="sm">
                  <Button asChild>
                    <Link to="/certifications/create">Create</Link>
                  </Button>
                </Media>
              </div>
              <div className="flex flex-1 h-full">
                {certifications.length === 0 &&
                name &&
                !searchLoading &&
                !pageLoading &&
                !loading ? (
                  <NoSearchResults
                    message={`No certifications found matching "${name}"`}
                    onClearSearch={handleClearSearch}
                  />
                ) : (
                  <CertificationsList certifications={certifications} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="py-4">
        <Pagination
          total={total}
          page={page}
          size={size}
          onPageChange={handlePageChange}
          pageLoading={pageLoading}
        />
      </div>
      <CertificationDrawer
        workspaceName={currentWorkspace.name}
        workspaceLogo={currentWorkspace.logo ?? ""}
        isOpen={Boolean(isCreateOpen)}
        title="Create Certification"
        onOpenChange={onOpenChange}
        onSubmit={createProps.onCreate}
        {...createProps}
      />
    </div>
  );
}
